import { useCallback, useContext } from 'react'
import type { Effect, MetroMapEvent, MetroMapState, Store } from './metroMapStore'
import { MetroMapStoreContext } from './metroMapStoreContext'
import { runEffects } from './effects'
import { realMetroAnalyticsReporter } from '../analytics/webAnalyticsReporter'

/**
 * Read-handle to the MetroMap store provided by
 * `<MetroMapStoreProvider>`. Throws if called outside a provider
 * (happens in tests that mount the canvas without the app shell).
 */
export function useMetroMapStore(): Store<MetroMapState, MetroMapEvent> {
  const store = useContext(MetroMapStoreContext)
  if (store === null) {
    throw new Error(
      'useMetroMapStore must be used inside <MetroMapStoreProvider>. ' +
        'Most likely this component is rendered outside <AppShell>.',
    )
  }
  return store
}

/**
 * Convenience wrapper returning a memoized `dispatch` that also runs
 * the side-effect runner (logger + analytics reporter). Use this from
 * any consumer that needs to dispatch — avoids each consumer
 * re-wiring effect plumbing.
 */
export function useMetroMapDispatch(): (event: MetroMapEvent) => readonly Effect[] {
  const store = useMetroMapStore()
  return useCallback(
    (event: MetroMapEvent) => {
      const effects = store.dispatch(event)
      runEffects(effects, {
        logger: (m) => console.log(m),
        reporter: realMetroAnalyticsReporter,
      })
      return effects
    },
    [store],
  )
}
